import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, InputLabel, Select, Switch } from '@components/Common';
import { FilterContainer } from '@components/Common/EntryFilters';
import DateFilterControlled from '@components/Common/EntryFilters/DateFilterControlled';
import FormLoader from '@components/Common/Loaders/FormLoader';
import NavBar from '@components/Pages/Reports/Common/ReportNavigation';
import { useTerminalsQuery } from '@api/queries/terminals';
import { useCustomersQuery } from '@api/queries/customers';
import { useDownloadCustomerReportMutation } from '@api/queries/reports';
import { useCustomerReportFilters } from '@hooks/useCustomerReportFilters';

// #region styles

const Columns = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;

  > div.column {
    box-sizing: border-box;
    width: 25%;
    padding-right: 15px;

    &.wide {
      flex-grow: 1;
    }

    &:last-child {
      padding-right: 0;
    }
  }
`;

const Column = styled.div`
  width: 25%;
  margin-right: 20px;
`;

const StyledSwitch = styled(Switch)`
  &:not(:first-child) {
    margin-left: 35px;
  }
`;

const CreateReportButton = styled(Button)`
  display: inline-block;
  padding: 10px 35px;
  text-decoration: none;

  border-right-width: 0;
  background: rgb(5, 195, 222);
  color: rgba(255, 255, 255);

  margin-left: 20px;
  margin-top: 40px;
  margin-bottom: 15px;
  border-right-width: 1px;
`;

// #endregion

const CustomersReporting: React.FC = () => {
  const { t } = useTranslation();

  // URL-based filters
  const {
    filters,
    dateFilter,
    setCustomerId,
    setTerminalId,
    setDateFilter,
    setGroupByReference,
    setGroupByLoading,
    setIncludeInvoicingInfo,
  } = useCustomerReportFilters();

  // Fetch reference data using TanStack Query
  const { data: terminals = [] } = useTerminalsQuery();
  const { data: customers = [] } = useCustomersQuery();

  // Report download mutation
  const downloadMutation = useDownloadCustomerReportMutation();

  const isLoading = downloadMutation.isPending;

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === filters.customerId),
    [customers, filters.customerId]
  );

  const customerOptions = useMemo(
    () => customers.map((c) => ({ value: c.id, label: c.name })),
    [customers]
  );

  const selectedCustomerOption = useMemo(
    () => customerOptions.find((c) => c.value === filters.customerId),
    [customerOptions, filters.customerId]
  );

  const terminalOptions = useMemo(
    () =>
      terminals
        .filter((t) => selectedCustomer?.terminalIds?.includes(t.id))
        .map((t) => ({ value: t.id, label: t.name })),
    [terminals, selectedCustomer]
  );

  const selectedTerminal = terminalOptions.find((t) => t.value === filters.terminalId);

  const customerChanged = useCallback(
    (newCustomerId: string | null) => {
      const customer = customers.find((c) => c.id === newCustomerId);
      setCustomerId(newCustomerId);
      // Auto-select first terminal when customer changes
      setTerminalId(customer?.terminalIds?.length ? customer.terminalIds[0] : null);
    },
    [customers, setCustomerId, setTerminalId]
  );

  const terminalChanged = useCallback(
    (newTerminalId: string | null) => {
      setTerminalId(newTerminalId);
    },
    [setTerminalId]
  );

  const createReport = useCallback(() => {
    downloadMutation.mutate({
      customerId: filters.customerId,
      terminalId: filters.terminalId,
      from: filters.startDate,
      to: filters.endDate,
      groupByReference: filters.groupByReference,
      groupByLoading: filters.groupByLoading,
      includeInvoicingInfo: filters.includeInvoicingInfo,
    });
  }, [downloadMutation, filters]);

  const isReportCreationDisabled =
    !filters.customerId || !filters.terminalId || !filters.startDate || !filters.endDate;

  return (
    <>
      <NavBar />
      <FilterContainer>
        <Columns>
          <Column>
            <InputLabel htmlFor="customerId">{t('entry.customer')}</InputLabel>
            <Select
              name="customerId"
              placeholder={t('entry.select_customer')}
              value={selectedCustomerOption ? selectedCustomerOption : { value: '', label: '' }}
              onChange={(i: { value: string } | null, j: { action: string }) =>
                customerChanged(j.action === 'clear' ? null : i?.value ?? null)
              }
              options={customerOptions}
            />
          </Column>
          <Column>
            <InputLabel htmlFor="terminalId">{t('entry.terminal')}</InputLabel>
            <Select
              name="terminalId"
              placeholder={t('select_terminal')}
              value={selectedTerminal ? selectedTerminal : { value: '', label: '' }}
              onChange={(i: { value: string } | null, j: { action: string }) =>
                terminalChanged(j.action === 'clear' ? null : i?.value ?? null)
              }
              options={terminalOptions}
            />
          </Column>
          <DateFilterControlled value={dateFilter} onChange={setDateFilter} />
          {isLoading && <FormLoader title={t('reports.loading')} />}
          <CreateReportButton onClick={createReport} disabled={isReportCreationDisabled}>
            {t('reports.create_report')}
          </CreateReportButton>
        </Columns>
        <Columns>
          <StyledSwitch
            label={t('reports.select_reference_grouping')}
            onChange={(i: boolean) => setGroupByReference(i)}
            checked={filters.groupByReference}
          />
          <StyledSwitch
            label={t('reports.select_loading_grouping')}
            onChange={(i: boolean) => setGroupByLoading(i)}
            checked={filters.groupByLoading}
          />
          <StyledSwitch
            label={t('reports.include_invoicing')}
            onChange={(i: boolean) => setIncludeInvoicingInfo(i)}
            checked={filters.includeInvoicingInfo}
          />
        </Columns>
      </FilterContainer>
    </>
  );
};

export default CustomersReporting;
